import React, { useContext, useEffect, useRef, useState } from "react";
import "./NewNav.css";
import Logo from "./Logo";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShopContext } from "../context/ShopContext";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { api } from "../api";
import NotificationBell from "./NotificationBell";
import { FaSearch, FaTimes, FaChevronDown, FaUser, FaDownload, FaShoppingCart, FaSignOutAlt, FaCoins } from "react-icons/fa";

const NewNav = () => {
  const { getTotalCartItems } = useContext(ShopContext);
  const { user, isLoggedIn, loading, logout } = useAuth();
  const cartCount  = getTotalCartItems();
  const navigate   = useNavigate();
  const { pathname } = useLocation();

  const [query,      setQuery]      = useState("");
  const [menuOpen,   setMenuOpen]   = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [acctOpen,   setAcctOpen]   = useState(false);
  const { dark: darkMode, toggle: toggleDark } = useTheme();
  const [unread,     setUnread]     = useState(0);

  const acctRef   = useRef(null);
  const navRef    = useRef(null);
  const postHref  = isLoggedIn || loading ? "/dashboard/post" : "/login?next=%2Fdashboard%2Fpost";
  const firstName = user?.name ? user.name.split(" ")[0] : null;
  const initial   = firstName ? firstName[0].toUpperCase() : "?";

  // Publish the navbar's real height as --nav-h so sticky elements on other
  // pages (e.g. the All Listings search bar) sit flush underneath it.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const setH = () =>
      document.documentElement.style.setProperty("--nav-h", `${el.offsetHeight}px`);
    setH();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", setH);
      return () => window.removeEventListener("resize", setH);
    }
    const ro = new ResizeObserver(setH);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Close dropdown on route change or outside click
  useEffect(() => { setAcctOpen(false); setMenuOpen(false); }, [pathname]);
  useEffect(() => {
    if (!acctOpen) return;
    const onDown = (e) => { if (acctRef.current && !acctRef.current.contains(e.target)) setAcctOpen(false); };
    const onKey  = (e) => { if (e.key === "Escape") setAcctOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown",   onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [acctOpen]);

  // Unread message count
  useEffect(() => {
    if (!isLoggedIn) { setUnread(0); return; }
    let live = true;
    const load = () => api("/me/unread-count").then(d => live && setUnread(d.count || 0)).catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => { live = false; clearInterval(t); };
  }, [isLoggedIn]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    setSearchOpen(false);
    setQuery("");
  };

  return (
    <>
      {/* ── Announcement bar ── */}
      <div className="nb-announce">
        <div className="nb-announce-inner">
          <span className="nb-announce-dot" />
          <span>Buy and sell safely with TrovePay escrow protection</span>
          <div className="nb-announce-right">
            <Link to="/all">Browse listings</Link>
            <span className="nb-announce-sep">|</span>
            <a href="tel:+2349034808095">Support: trovehub.ng</a>
          </div>
        </div>
      </div>

      {/* ── Main navbar ── */}
      <nav className="navbar" ref={navRef}>

        {/* Logo */}
        <Link to="/" className="navbar-logo" aria-label="TroveHub home">
          <Logo />
        </Link>

        {/* ── Desktop centre: links + search ── */}
        <div className="navbar-centre">
          <Link to="/companies" className="nb-nav-link">
            <span className="nb-nav-icon">🏬</span> Stores
          </Link>

          <div className="nb-location">
            <FaSearch className="nb-loc-icon" />
            <span>Nigeria</span>
            <FaChevronDown className="nb-loc-caret" />
          </div>

          {/* Search */}
          <form className="navbar-search" onSubmit={handleSearch}>
            <input
              type="text"
              placeholder="Search cars, phones, houses, solar..."
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <button type="submit" className="nb-search-btn" aria-label="Search">
              <FaSearch />
            </button>
          </form>
        </div>

        {/* ── Desktop right ── */}
        <div className="navbar-actions">
          {/* Dark mode toggle */}
          <button
            type="button"
            className={`nb-toggle ${darkMode ? "on" : ""}`}
            onClick={toggleDark}
            aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            <span className="nb-toggle-thumb">
              {darkMode ? "🌙" : "☀️"}
            </span>
          </button>

          {/* Cart */}
          <Link to="/cart" className="nb-icon-btn" aria-label="Cart">
            <FaShoppingCart size={20} />
            {cartCount > 0 && <span className="nb-dot">{cartCount}</span>}
          </Link>

          {isLoggedIn && <NotificationBell />}

          {/* User account pill */}
          {isLoggedIn ? (
            <div className="nb-acct" ref={acctRef}>
              <button
                type="button"
                className="nb-acct-btn"
                onClick={() => setAcctOpen(o => !o)}
                aria-haspopup="true"
                aria-expanded={acctOpen}
              >
                {user?.avatar
                  ? <img className="nb-avatar" src={user.avatar} alt={firstName} />
                  : <span className="nb-avatar nb-avatar-init">{initial}</span>
                }
                <span className="nb-acct-name">{firstName}</span>
                <FaChevronDown className={`nb-acct-caret${acctOpen ? " open" : ""}`} />
              </button>

              {acctOpen && (
                <div className="nb-dropdown" role="menu">
                  <Link to="/dashboard"          role="menuitem" onClick={() => setAcctOpen(false)}>
                    <FaUser />        My Account
                  </Link>
                  <Link to="/dashboard/escrow"   role="menuitem" onClick={() => setAcctOpen(false)}>
                    <FaDownload />    My Purchases
                  </Link>
                  <Link to="/dashboard/finance"  role="menuitem" onClick={() => setAcctOpen(false)}>
                    <FaCoins />       Earnings
                    {unread > 0 && <span className="nb-dd-badge">{unread}</span>}
                  </Link>
                  <div className="nb-dd-sep" />
                  <button type="button" role="menuitem" className="nb-dd-logout" onClick={logout}>
                    <FaSignOutAlt />  Log out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="nb-login-btn">Log in</Link>
          )}

          {/* Post Ad */}
          <Link to={postHref} className="nb-post-btn">
            <span className="nb-post-plus">+</span> Post Ad
          </Link>
        </div>

        {/* ── Mobile icons ── */}
        <div className="navbar-mobile-icons">
          <button className="nb-icon-btn" onClick={() => setSearchOpen(true)} aria-label="Search">
            <FaSearch size={18} />
          </button>

          <button
            type="button"
            className={`nb-toggle nb-toggle-sm ${darkMode ? "on" : ""}`}
            onClick={toggleDark}
            aria-label="Toggle dark mode"
          >
            <span className="nb-toggle-thumb">{darkMode ? "🌙" : "☀️"}</span>
          </button>

          <Link to="/cart" className="nb-icon-btn" aria-label="Cart">
            <FaShoppingCart size={20} />
            {cartCount > 0 && <span className="nb-dot">{cartCount}</span>}
          </Link>

          {isLoggedIn ? (
            <button
              type="button"
              className="nb-avatar nb-avatar-init nb-avatar-mob"
              onClick={() => setMenuOpen(true)}
              aria-label="Menu"
            >
              {initial}
            </button>
          ) : (
            <button className="nb-icon-btn" onClick={() => setMenuOpen(true)} aria-label="Menu">
              <FaUser size={20} />
            </button>
          )}
        </div>
      </nav>

      {/* ── Mobile search overlay ── */}
      {searchOpen && (
        <div className="search-overlay" onClick={() => setSearchOpen(false)}>
          <div className="search-overlay-inner" onClick={e => e.stopPropagation()}>
            <form onSubmit={handleSearch}>
              <FaSearch className="sol-icon" />
              <input
                autoFocus
                type="text"
                placeholder="Search listings..."
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
              <button type="button" onClick={() => setSearchOpen(false)}>
                <FaTimes size={18} />
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── Mobile slide-up menu ── */}
      {menuOpen && (
        <div className="mob-overlay" onClick={() => setMenuOpen(false)}>
          <div className="mob-menu" onClick={e => e.stopPropagation()}>
            <div className="mob-menu-header">
              <Logo className="mob-logo" />
              <button onClick={() => setMenuOpen(false)} className="mob-close">
                <FaTimes size={20} />
              </button>
            </div>

            {isLoggedIn && (
              <div className="mob-user-row">
                <span className="mob-avatar">{initial}</span>
                <div>
                  <div className="mob-user-name">{user?.name}</div>
                  <div className="mob-user-email">{user?.email}</div>
                </div>
              </div>
            )}

            <ul className="mob-menu-list">
              <li onClick={() => setMenuOpen(false)}>
                <Link to={postHref} className="mob-post-item">
                  <span className="mml-icon sell">+</span>
                  <span>Post a Free Ad</span>
                  <span className="mml-arrow">›</span>
                </Link>
              </li>

              {isLoggedIn ? (
                <>
                  <li onClick={() => setMenuOpen(false)}>
                    <Link to="/dashboard">
                      <span className="mml-icon"><FaUser /></span>
                      <span>My Account</span>
                      <span className="mml-arrow">›</span>
                    </Link>
                  </li>
                  <li onClick={() => setMenuOpen(false)}>
                    <Link to="/dashboard/escrow">
                      <span className="mml-icon"><FaDownload /></span>
                      <span>My Purchases</span>
                      <span className="mml-arrow">›</span>
                    </Link>
                  </li>
                  <li onClick={() => setMenuOpen(false)}>
                    <Link to="/dashboard/finance">
                      <span className="mml-icon"><FaCoins /></span>
                      <span>Earnings</span>
                      <span className="mml-arrow">›</span>
                    </Link>
                  </li>
                  <li className="mml-divider" />
                  <li onClick={() => { setMenuOpen(false); logout(); }} className="mob-logout-item">
                    <span className="mml-icon"><FaSignOutAlt /></span>
                    <span>Log out</span>
                    <span className="mml-arrow">›</span>
                  </li>
                </>
              ) : (
                <>
                  <li onClick={() => setMenuOpen(false)}>
                    <Link to="/login">
                      <span className="mml-icon"><FaUser /></span>
                      <span>Log In</span>
                      <span className="mml-arrow">›</span>
                    </Link>
                  </li>
                  <li onClick={() => setMenuOpen(false)}>
                    <Link to="/login">
                      <span className="mml-icon">+</span>
                      <span>Register</span>
                      <span className="mml-arrow">›</span>
                    </Link>
                  </li>
                </>
              )}

              <li className="mml-divider" />
              <li onClick={() => setMenuOpen(false)}>
                <Link to="/companies">
                  <span className="mml-icon">🏬</span>
                  <span>Stores</span>
                  <span className="mml-arrow">›</span>
                </Link>
              </li>
              <li onClick={() => setMenuOpen(false)}>
                <Link to="/all">
                  <span className="mml-icon"><FaSearch /></span>
                  <span>Browse Listings</span>
                  <span className="mml-arrow">›</span>
                </Link>
              </li>
              <li onClick={() => setMenuOpen(false)}>
                <Link to="/contact">
                  <span className="mml-icon">✉️</span>
                  <span>Contact Us</span>
                  <span className="mml-arrow">›</span>
                </Link>
              </li>
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default NewNav;